import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { debounce } from 'lodash'
import { fromEvent } from 'rxjs'
import { pairwise, switchMap, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'mmx-canvas',
  template: `<canvas #canvas></canvas>
    <button
      type="button"
      color="warn"
      (click)="clearCanvas()"
      mat-icon-button
      matTooltip="Clear"
    >
      <mat-icon>clear</mat-icon>
    </button>`,
  styles: ['canvas { border: 1px solid #000; }'],
  standalone: false,
})
export class CanvasComponent implements AfterViewInit {
  @ViewChild('canvas', { static: false }) canvas: ElementRef

  @Input() public width = 400
  @Input() public height = 400
  @Input() public existingImageSrc: string
  @Input() public imageSrc: string
  @Output() public data = new EventEmitter<string>()

  private cx: CanvasRenderingContext2D

  constructor() {
    this.emit = debounce(this.emit.bind(this))
  }

  public ngAfterViewInit() {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement
    this.cx = canvasEl.getContext('2d')

    canvasEl.width = this.width
    canvasEl.height = this.height

    this.cx.lineWidth = 3
    this.cx.lineCap = 'round'
    this.cx.strokeStyle = '#58B7A4'
    if (this.existingImageSrc || this.imageSrc) {
      this.setImage(this.existingImageSrc || this.imageSrc)
    }
    this.captureEvents(canvasEl)
  }

  clearCanvas() {
    this.cx.clearRect(0, 0, this.width, this.height)

    if (this.imageSrc) {
      this.setImage(this.imageSrc)
    }
  }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap(() => {
          return fromEvent(canvasEl, 'mousemove').pipe(
            takeUntil(fromEvent(canvasEl, 'mouseup')),
            pairwise(),
          )
        }),
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect()

        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top,
        }

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top,
        }

        this.drawOnCanvas(prevPos, currentPos)
      })

    fromEvent(canvasEl, 'touchstart')
      .pipe(
        switchMap(() => {
          return fromEvent(canvasEl, 'touchmove').pipe(
            takeUntil(fromEvent(canvasEl, 'touchend')),
            pairwise(),
          )
        }),
      )
      .subscribe((res: [TouchEvent, TouchEvent]) => {
        const rect = canvasEl.getBoundingClientRect()

        const prevPos = {
          x: res[0].touches[0].clientX - rect.left,
          y: res[0].touches[0].clientY - rect.top,
        }

        const currentPos = {
          x: res[1].touches[0].clientX - rect.left,
          y: res[1].touches[0].clientY - rect.top,
        }
        this.drawOnCanvas(prevPos, currentPos)
      })
  }

  private drawOnCanvas(
    prevPos: { x: number; y: number },
    currentPos: { x: number; y: number },
  ) {
    if (!this.cx) {
      return
    }

    this.cx.beginPath()

    if (prevPos) {
      this.cx.moveTo(prevPos.x, prevPos.y) // from
      this.cx.lineTo(currentPos.x, currentPos.y)
      this.cx.stroke()
      this.emit()
    }
  }

  private setImage(dataURL: string) {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      this.cx.drawImage(
        image,
        0,
        0,
        image.width,
        image.height,
        0,
        0,
        this.width,
        this.height,
      )
    }
    image.src = dataURL
  }

  private emit() {
    const element = this.canvas.nativeElement as HTMLCanvasElement
    const imageData = element.toDataURL('image/png')
    this.data.emit(imageData)
  }
}
